import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeConfig } from 'antd';
import { ConfigProvider, theme as antdTheme } from 'antd';
import ruRU from 'antd/locale/ru_RU';

export type AppTheme = 'dark' | 'light';

const THEME_STORAGE_KEY = 'mg_color_theme';
const DEFAULT_THEME: AppTheme = 'dark';

type ThemeContextValue = {
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const normalizeTheme = (value: unknown): AppTheme | null => (
  value === 'dark' || value === 'light' ? value : null
);

export const getNextTheme = (theme: AppTheme): AppTheme => (theme === 'dark' ? 'light' : 'dark');

const loadInitialTheme = (): AppTheme => {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    return normalizeTheme(window.localStorage.getItem(THEME_STORAGE_KEY)) ?? DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

const applyDocumentTheme = (theme: AppTheme): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = theme === 'light' ? '#f4f7fb' : '#141619';
  }
};

const buildAntTheme = (theme: AppTheme): ThemeConfig => {
  const isLight = theme === 'light';
  return {
    algorithm: isLight ? antdTheme.defaultAlgorithm : antdTheme.darkAlgorithm,
    token: {
      colorBgBase: isLight ? '#f4f7fb' : '#141619',
      colorBgContainer: isLight ? '#ffffff' : '#252b36',
      colorBgElevated: isLight ? '#ffffff' : '#1b202c',
      colorBorder: isLight ? '#d9e2ec' : '#2f3542',
      colorText: isLight ? '#172033' : '#f8fafc',
      colorTextSecondary: isLight ? '#536174' : '#d9e2ef',
      colorTextTertiary: isLight ? '#718096' : '#94a3b8',
      colorPrimary: isLight ? '#2563eb' : '#60a5fa',
      borderRadius: 8,
      boxShadow: isLight ? '0 8px 20px rgba(15, 23, 42, 0.10)' : '0 8px 18px rgba(3, 8, 14, 0.22)',
    },
  };
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<AppTheme>(() => loadInitialTheme());

  useEffect(() => {
    applyDocumentTheme(theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Storage can be unavailable in private browsing modes.
    }
  }, [theme]);

  const setTheme = useCallback((next: AppTheme) => {
    setThemeState(normalizeTheme(next) ?? DEFAULT_THEME);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => getNextTheme(prev));
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme, toggleTheme }), [setTheme, theme, toggleTheme]);
  const antTheme = useMemo(() => buildAntTheme(theme), [theme]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider locale={ruRU} theme={antTheme}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useThemePreference = (): ThemeContextValue => {
  const value = useContext(ThemeContext);
  if (!value) {
    throw new Error('useThemePreference must be used within ThemeProvider');
  }
  return value;
};
